import { debounce } from 'debounce';

import { Breakpoints } from '../../../enums';
import { ReactTable } from '../../../types';

const LOCAL_STORAGE_KEY = 'savedColumnWidths';

const getBreakpoint = (width: number): Breakpoints => {
  for (const breakpoint of [
    Breakpoints.small,
    Breakpoints.medium,
    Breakpoints.large,
    Breakpoints.extraLarge,
    Breakpoints.extraExtraLarge,
    Breakpoints.extraExtraExtraLarge,
  ]) {
    if (width < breakpoint) {
      return breakpoint;
    }
  }
  return Breakpoints.extraExtraExtraLarge;
};

class ColumnWidthManager {
  tableId: string;
  defaultWidths: Record<string, number>;
  visibleColumnIds: string[];

  // tableId -> breakpoint -> columnId -> columnWidth
  customWidths: Record<string, Record<string, Record<string, number>>>;
  lastUsedWidths: Record<string, number>;

  constructor(tableId: string, visibleColumnIds: string[]) {
    this.tableId = tableId;
    this.visibleColumnIds = visibleColumnIds;
    this.defaultWidths = {};
    this.lastUsedWidths = {};
    this.loadSavedWidths();
  }

  updateDefaultWidths(columns: ReactTable.Column[]): void {
    for (const column of columns) {
      this.defaultWidths[column.id] = column.width;
    }
  }

  loadSavedWidths(): void {
    try {
      this.customWidths = JSON.parse(localStorage[LOCAL_STORAGE_KEY]);
    } catch {
      this.customWidths = {};
    }
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  saveWidths = debounce(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.customWidths));
  }, 200);

  updateVisibleColumnIds(visibleColumnIds: string[]): void {
    this.visibleColumnIds = visibleColumnIds;
  }

  updateWidth(tableWidth: number, columnId: string, width: number): void {
    const breakpoint = String(getBreakpoint(tableWidth));

    if (!this.customWidths[this.tableId]) {
      this.customWidths[this.tableId] = {};
    }
    const savedWidthsForTable = this.customWidths[this.tableId];
    if (!savedWidthsForTable[breakpoint]) {
      savedWidthsForTable[breakpoint] = {};
    }
    const savedWidthsForTableAndBreakpoint = savedWidthsForTable[breakpoint];

    for (const _columnId of this.visibleColumnIds) {
      if (_columnId === columnId) {
        this.lastUsedWidths[_columnId] = width;
        savedWidthsForTableAndBreakpoint[_columnId] = width;
      } else if (this.lastUsedWidths[_columnId]) {
        savedWidthsForTableAndBreakpoint[_columnId] =
          this.lastUsedWidths[_columnId];
      }
    }
    this.saveWidths();
  }

  applyColumnWidths(
    tableWidth: number,
    columns: ReactTable.Column[]
  ): ReactTable.Column[] {
    return columns.map(column => ({
      ...column,
      width: this.getColumnGrownWidth(tableWidth, column.id),
    }));
  }

  getColumnGrownWidth(tableWidth: number, columnId: string): number {
    const breakpoint = String(getBreakpoint(tableWidth));
    const totalVisibleWidth = this.getTotalVisibleWidth(breakpoint);
    const scaleRatio =
      totalVisibleWidth < tableWidth ? tableWidth / totalVisibleWidth : 1;
    const grownWidth =
      scaleRatio *
      (this.getSavedWidth(breakpoint, columnId) ||
        this.getDefaultWidth(columnId));
    this.lastUsedWidths[columnId] = grownWidth;

    // Due to [sc-3354], some users have huge column widths in their local storage.
    // this code should help them to reset those over-grown column widths.
    if (grownWidth >= tableWidth) {
      console.error(
        `The column ${columnId} is too wide (greater than table width), resetting to default.`
      );
      return this.getDefaultWidth(columnId);
    }

    return grownWidth;
  }

  private getSavedWidth(
    breakpoint: string,
    columnId: string
  ): number | undefined {
    if (
      !this.customWidths[this.tableId] ||
      !this.customWidths[this.tableId][breakpoint]
    ) {
      return null;
    }
    return this.customWidths[this.tableId][breakpoint][columnId];
  }

  private getDefaultWidth(columnId): number {
    return this.defaultWidths[columnId] || 100;
  }

  private getTotalVisibleWidth = (breakpoint: string): number => {
    return this.visibleColumnIds
      .map(
        columnId =>
          this.getSavedWidth(breakpoint, columnId) ||
          this.getDefaultWidth(columnId)
      )
      .reduce((pre, cur) => pre + cur, 0);
  };
}

export default ColumnWidthManager;
